import { Dropdown, Option, OptionOnSelectData, SelectionEvents } from '@fluentui/react-components'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { Button } from '../Button'
import { ProjectAdminCard } from '../FluentCard/TeamMemberCard'
import { Loading } from '../Loading'
import { isProjectAdmin } from '@/util/roles'
import { useGetSearchParam } from '@/hooks'

type MessagesProjectsOrganisationProject = RestSchemas['Messages.Projects.OrganisationProject']
type MessagesSubscriptionsOrganisationUser = RestSchemas['Messages.Subscriptions.OrganisationUser']

type MaintainProjectAdminProps = {
  selectedProject: MessagesProjectsOrganisationProject
}

export const MaintainProjectAdmins = (props: MaintainProjectAdminProps) => {
  const queryClient = useQueryClient()
  const {
    getSubscriptionsApiListOrganisationUsers,
    putProjectsApiAddProjectAdmin,
    deleteProjectsApiRemoveProjectAdmin,
    getProjectsApiListOrganisationProjects,
  } = createRestClient()

  const orgId = useGetSearchParam<number>('orgId')

  const {
    data: adminUsers,
    isPending,
    isError,
  } = getSubscriptionsApiListOrganisationUsers.useQuery(
    {
      query: {
        orgId,
      },
    },
    {
      // select: (data) => data.filter((user) => isAdmin(user.roles)),
      select: (data) => data.filter((user) => isProjectAdmin(user.roles)),
      enabled: !!orgId,
    }
  )

  const onMutationSuccess = () => {
    getProjectsApiListOrganisationProjects.invalidateQueries({ parameters: { query: { orgId } } }, queryClient)
  }

  const { mutate: addAdmin } = putProjectsApiAddProjectAdmin.useMutation()
  const { mutate: deleteAdmin } = deleteProjectsApiRemoveProjectAdmin.useMutation()

  const [selectedOptions, setSelectedOptions] = useState<MessagesSubscriptionsOrganisationUser[]>([])

  const onOptionSelect = (_event: SelectionEvents, data: OptionOnSelectData) => {
    const _selectedOptions = adminUsers?.filter((user) => data.selectedOptions.includes(user.id.toString())) ?? []
    setSelectedOptions(_selectedOptions)
  }

  const onAddSubmit = () => {
    selectedOptions.forEach((admin) => {
      addAdmin(
        { query: { adminId: admin.id, projectId: props.selectedProject.projectId } },
        {
          onSuccess: () => {
            toast.success('Admin successfully added')
            onMutationSuccess()
          },
        }
      )
    })
    setSelectedOptions([])
  }

  const onDelete = (adminId: number) => {
    deleteAdmin(
      { query: { adminId, projectId: props.selectedProject.projectId } },
      {
        onSuccess: () => {
          toast.success('Admin successfully removed')
          onMutationSuccess()
        },
      }
    )
  }

  // TODO: Add error handling and loading state
  if (isPending) return null
  if (isError) return null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', paddingBottom: '20px' }}>
      <Dropdown
        aria-labelledby={'add-org-user-as-project-admin'}
        multiselect={true}
        placeholder="Add team members"
        value={selectedOptions.map((option) => `${option.givenName} ${option.familyName}`).join(', ')}
        selectedOptions={selectedOptions.map((option) => option.id.toString())}
        onOptionSelect={onOptionSelect}
      >
        <div style={{ maxHeight: '600px', msOverflowY: 'scroll' }}>
          {/* {projectAdminOptions.map((option) => ( */}
          {adminUsers?.map((option) => (
            <Option
              key={option.id}
              disabled={props.selectedProject.administrators.map((admin) => admin.userId).includes(option.id)}
              value={option.id.toString()}
            >
              {`${option.givenName} ${option.familyName}`}
            </Option>
          ))}
        </div>
      </Dropdown>
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'end' }}>
        <Button text="Add members" onClick={onAddSubmit} />
      </div>
      {isPending && <Loading />}
      {!isPending &&
        props.selectedProject.administrators.map((admin) => (
          <ProjectAdminCard key={admin.userId} admin={admin} onDelete={() => onDelete(admin.userId)} />
        ))}
    </div>
  )
}
